import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import debounce from "lodash/debounce";
import axiosClient from "../axios";
import { NONE_OFFER_OPTION } from "./offerTargeting";

export const formatOfferOptionLabel = (option) => {
  if (!option?._id) return option?.name || "None";
  const name = option.name || option._id;
  const pOfferId = option.p_offer_id
    ? `(${option.p_offer_id}) `
    : "";
  const partner = option.partnerName ? ` — ${option.partnerName}` : "";
  return `${pOfferId}${name}${partner}`;
};

export default function DependsOnOfferSearch({
  offerId,
  initialOption = null,
  excludeOfferId = null,
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(NONE_OFFER_OPTION);

  useEffect(() => {
    if (!offerId) {
      setSelected(NONE_OFFER_OPTION);
      return;
    }

    if (initialOption && String(initialOption._id) === String(offerId)) {
      setSelected(initialOption);
      return;
    }

    setSelected((prev) => {
      if (prev?._id && String(prev._id) === String(offerId)) {
        return prev;
      }
      return {
        _id: String(offerId),
        name: String(offerId),
        p_offer_id: "",
        partnerName: "",
      };
    });
  }, [offerId, initialOption]);

  const fetchOffers = useCallback(
    async (search) => {
      const trimmed = search.trim();
      if (!trimmed) {
        setSearchResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams({
          search: trimmed,
          limit: "20",
        });
        if (excludeOfferId) {
          params.set("excludeId", String(excludeOfferId));
        }

        const res = await axiosClient.get(`/admin/search-offers?${params.toString()}`);
        const offers = (res?.data?.response?.offers || []).map((offer) => ({
          _id: String(offer._id),
          name: offer.name || String(offer._id),
          p_offer_id: offer.p_offer_id || "",
          partnerName: offer.partnerName || "",
        }));
        setSearchResults(offers);
      } catch (error) {
        console.error("Error searching offers:", error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    },
    [excludeOfferId]
  );

  const debouncedSearch = useMemo(
    () => debounce((search) => fetchOffers(search), 500),
    [fetchOffers]
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  useEffect(() => {
    if (open) {
      debouncedSearch(searchText);
    }
  }, [searchText, open, debouncedSearch]);

  const options = useMemo(() => {
    const list = [NONE_OFFER_OPTION, ...searchResults];
    if (
      selected?._id &&
      !list.some((option) => String(option._id) === String(selected._id))
    ) {
      list.splice(1, 0, selected);
    }
    return list;
  }, [searchResults, selected]);

  const handleOpen = () => {
    setOpen(true);
    setSearchText("");
    setSearchResults([]);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchText("");
    setSearchResults([]);
    debouncedSearch.cancel();
  };

  const closedLabel = formatOfferOptionLabel(selected);

  return (
    <Autocomplete
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options}
      filterOptions={(x) => x}
      loading={loading}
      value={selected}
      inputValue={open ? searchText : closedLabel}
      onInputChange={(event, newInputValue, reason) => {
        if (!open) return;
        if (reason === "input") {
          setSearchText(newInputValue);
        }
      }}
      onChange={(event, newValue) => {
        const next = newValue || NONE_OFFER_OPTION;
        setSelected(next);
        onChange(newValue?._id ?? null, newValue || null);
        handleClose();
      }}
      getOptionLabel={formatOfferOptionLabel}
      isOptionEqualToValue={(option, value) =>
        String(option?._id ?? "") === String(value?._id ?? "")
      }
      openOnFocus
      clearOnBlur={false}
      forcePopupIcon
      noOptionsText={
        searchText.trim() ? "No offers found" : "Type offer name to search"
      }
      ListboxProps={{ style: { maxHeight: 280 } }}
      slotProps={{
        popper: {
          placement: "bottom-start",
          sx: { zIndex: 1400 },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Click to search and select offer"
          inputProps={{
            ...params.inputProps,
            readOnly: !open,
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={String(option._id ?? "none")}>
          {formatOfferOptionLabel(option)}
        </li>
      )}
    />
  );
}
